import { track } from '../analytics/ga.js';

/**
 * Browser fullscreen helpers.
 *
 * The Fullscreen API requires a user gesture, so callers must wire these to
 * onclick handlers. On installed PWAs (manifest display=fullscreen) the
 * browser already runs without chrome, so calling enter is a no-op there.
 */

interface FsDoc extends Document {
  webkitFullscreenElement?: Element;
  webkitExitFullscreen?: () => Promise<void>;
}
interface FsEl extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
}

export function isFullscreen(): boolean {
  const d = document as FsDoc;
  return !!(document.fullscreenElement || d.webkitFullscreenElement);
}

export function fullscreenSupported(): boolean {
  const root = document.documentElement as FsEl;
  return !!(root.requestFullscreen || root.webkitRequestFullscreen);
}

export async function enterFullscreen(): Promise<void> {
  const root = document.documentElement as FsEl;
  try {
    if (root.requestFullscreen) await root.requestFullscreen({ navigationUI: 'hide' });
    else if (root.webkitRequestFullscreen) await root.webkitRequestFullscreen();
  } catch { /* user denied or unsupported */ }
}

export async function exitFullscreen(): Promise<void> {
  const d = document as FsDoc;
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
  } catch { /* ignore */ }
}

export async function toggleFullscreen(): Promise<void> {
  if (isFullscreen()) return exitFullscreen();
  return enterFullscreen();
}

export function onFullscreenChange(fn: () => void): () => void {
  const handler = () => fn();
  document.addEventListener('fullscreenchange', handler);
  document.addEventListener('webkitfullscreenchange', handler);
  return () => {
    document.removeEventListener('fullscreenchange', handler);
    document.removeEventListener('webkitfullscreenchange', handler);
  };
}

/* ---- PWA install prompt capture (Android Chrome/Brave) ---- */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    window.dispatchEvent(new CustomEvent('wean:install-available'));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent('wean:install-available'));
  });
}

export function isInstallAvailable(): boolean { return deferredPrompt !== null; }

export function isStandalone(): boolean {
  // Either a real PWA launch or iOS home-screen mode.
  if (window.matchMedia?.('(display-mode: fullscreen)').matches) return true;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((navigator as Navigator & { standalone?: boolean }).standalone) return true;
  return false;
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) {
    track('install_prompt_shown', { outcome: 'unavailable' });
    return 'unavailable';
  }
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  window.dispatchEvent(new CustomEvent('wean:install-available'));
  track('install_prompt_shown', { outcome: choice.outcome });
  return choice.outcome;
}
